/**
 * Updates the recently viewed products in localStorage.
 *
 * Toran Cart change (re-apply after a Horizon update): every localStorage call is wrapped in try/catch, because
 * localStorage throws when the shopper's browser blocks site data (private windows, strict privacy settings).
 * Without it the product page script that records the view throws and the search popup stops working.
 * getProducts also keeps only product ids (whole numbers as strings), so a broken entry cannot reach a search.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    } catch {
      // Storage is blocked or full: the view is simply not remembered.
    }
  }

  static clearProducts() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
    } catch {
      // Storage is blocked: there is nothing to clear.
    }
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    try {
      const stored = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
      if (!Array.isArray(stored)) return [];
      return stored.map(String).filter((id) => /^\d+$/.test(id));
    } catch {
      return [];
    }
  }
}
